import React, { memo, useEffect, useMemo, useState } from 'react';

import {
	Chart,
	ChartControlPanel,
	CheckboxOptions,
	DateBreadcrumbs,
	ExportActionsTypes,
	FileTypeSelectionCommonForm,
	Modal,
	SliderCheckbox,
	SpinnerOverlay,
	ThresholdText } from '@components/common';
import KpiTags from '@components/common/KpiTags';

import { useQueryAvailableGranularityDrillUp } from '@hooks/kpis/useQueryAvailableGranularityDrillUp';

import { kpi } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { generateListOfCompareMessages, generateListOfCorrelateMessages } from '@utils/kpi-utils/chart';
import { checkIsMultiDays, getDateByTimeAggregationType } from '@utils/kpi-utils/date';

import { IExportKpiDTO, IKpiChartsDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IChart } from '@interfaces/kpi/kpiCommon';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';
import { IFullChartCardTypes } from './FullChartCard.types';

import { ETimeAggregationTypes, EVendorsValues } from '@enums/common';
import { EAggregationTypes } from '@enums/kpi-api';
import { EGranularitiesValues } from '@enums/kpi-common';

import { exportRadioButtonsData, timezoneUTC } from '@constants/common';
import { listOfTimeAggregationTypes } from '@constants/KPI/kpi-common';
import { MODAL_WIDTH_455 } from '@constants/modals';
import { GranularityToTimeAggregationMap, TimeAggregationToGranularityMap } from '@components/Kpi/Parameters/constants';

import './FullChartCard.scss';

const FullChartCard = ({
	chart,
	drillDownCharts,
	isWithInterval,
	getDrillDownCharts,
	isLoadingDrillDownCharts,
	compareSettings,
	addChartToCompare,
	compareCharts,
	correlateCharts,
	addChartToCorrelate,
	correlateSettings,
	exportCharts,
}: IFullChartCardTypes) => {
	const aggregationType = chart.aggregationType.name;
	const drillDownChart = drillDownCharts[0];
	const defaultIndexTimeAggregationType = listOfTimeAggregationTypes.indexOf(chart.yaxisAggregationType.name);
	const isOranVendor = chart.vendor.name === EVendorsValues.oran;
	const { chartsReceivedAt } = useAppSelector(kpi);

	const [isCheckedInterval, setIsCheckedInterval] = useState(false);
	const [defaultIndexTimeAggregationTypeBreadcrumbs, setDefaultIndexTimeAggregationTypeBreadcrumbs] =
		useState<number>(listOfTimeAggregationTypes.indexOf(chart.yaxisAggregationType.name));

	const [detailedDrillTime, setDetailedDrillTime] = useState({});
	const [currentIndexTimeAggregationType, setCurrentIndexTimeAggregationType]
		= useState<number>(defaultIndexTimeAggregationType);
	const [currentChart, setCurrentChart] = useState<IChart>(chart);

	const isMultiDays = checkIsMultiDays({
		points: currentChart.lines[0].points,
		organizationTimezone: timezoneUTC,
	});
	const firstDate = currentChart.lines[0]?.points[0]?.x;
	const lastDate = currentChart.lines[0]?.points[currentChart.lines[0]?.points.length - 1]?.x;
	const thresholds = currentChart?.thresholds;
	const isMultiLines = currentChart?.lines.length > 1;
	const isCheckedCompare = useMemo(() => compareCharts.some(compareChart =>
		compareChart.chartId === currentChart.chartId
		&& compareChart.yaxisAggregationType.name === currentChart.yaxisAggregationType.name,
	), [currentChart, compareCharts]);
	const isCheckedCorrelate = useMemo(() => correlateCharts.some(correlateChart =>
		correlateChart.chartId === currentChart.chartId
		&& correlateChart.yaxisAggregationType.name === currentChart.yaxisAggregationType.name,
	), [currentChart, correlateCharts]);
	const isShowSiteId = aggregationType !== EAggregationTypes.aggregated;
	const isShowLegendByAggregationType = aggregationType === EAggregationTypes.siteLevelAggregatedCells;
	const siteId = chart?.sites[0].siteId;
	const isView15MinCheckbox = (isOranVendor
			&& (currentChart.granularity?.name === EGranularitiesValues.every_15_minute
				|| !!currentChart?.isAvailableFifteenMinutes))
		|| (!isOranVendor && (currentChart.yaxisAggregationType.name === ETimeAggregationTypes.hours
			|| currentChart.yaxisAggregationType.name === ETimeAggregationTypes.interval));

	const [isThreshold, setIsThreshold] = useState(!!chart.isThreshold);
	const [isLogarithmic, setIsLogarithmic] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const toggleAddToCompare = () => addChartToCompare(currentChart);

	const toggleAddToCorrelate = () => addChartToCorrelate(currentChart);

	const listOfTooltipCompareMessage = useMemo(() => generateListOfCompareMessages({
		compareCharts,
		chart: currentChart,
		compareSettings,
		isEmptyChart: false,
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [compareSettings, currentChart]);

	const listOfTooltipCorrelateMessage = useMemo(() => generateListOfCorrelateMessages({
		correlateCharts,
		chart: currentChart,
		correlateSettings,
		isEmptyChart: false,
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [correlateSettings, currentChart]);

	useEffect(() => {
		if (!drillDownCharts.length) return;

		const shouldSetChart =
			isCheckedInterval ||
			currentChart.yaxisAggregationType.name === ETimeAggregationTypes.interval ||
			defaultIndexTimeAggregationType !== currentIndexTimeAggregationType;

		if (shouldSetChart || isOranVendor) {
			setCurrentChart(drillDownChart);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drillDownCharts]);

	useEffect(() => {
		setCurrentChart(chart);
	}, [chart]);

	const toggleThreshold = () => setIsThreshold(prevState => !prevState);

	const toggleLogarithmic = () => setIsLogarithmic(prevState => !prevState);

	const setIntervalData = (_id: string, isChecked: boolean) => {
		setIsCheckedInterval(prevState => !prevState);

		const start = currentChart.from;
		const end = currentChart.to;
		const prevTime = (detailedDrillTime as any)[ETimeAggregationTypes.hours];

		const preparedTimeAggregationType = isOranVendor
			? undefined
			: (isChecked ? ETimeAggregationTypes.interval : ETimeAggregationTypes.hours);
		const granularity = isOranVendor
			? (isChecked ? EGranularitiesValues.every_15_minute : EGranularitiesValues.hourly)
			: undefined;

		const drillDownData: IKpiChartsDTO = {
			from: isChecked ? start : prevTime?.from || currentChart.from,
			to: isChecked ? end : prevTime?.to || currentChart.to,
			aggregationType: aggregationType,
			kpiIds: [currentChart.kpiId],
			sites: currentChart.sites,
			timeAggregationType: preparedTimeAggregationType,
			vendor: currentChart.vendor.name,
			granularity,
		};

		getDrillDownCharts(drillDownData);
	};

	const drillDown = (dates: Array<number>, isAvailableDrillDown?: boolean) => {
		if (isCheckedInterval) return;

		if (currentIndexTimeAggregationType - 1 < 0) return;

		const selectedTimeAggregationType = listOfTimeAggregationTypes[currentIndexTimeAggregationType - 1];

		const start = getDateByTimeAggregationType({
			date: dates[0],
			timeAggregationType: selectedTimeAggregationType,
			isStartOfDate: true,
			chartEndTo: currentChart.to,
		});

		const end = getDateByTimeAggregationType({
			date: dates[0],
			timeAggregationType: selectedTimeAggregationType,
			isStartOfDate: false,
			chartEndTo: currentChart.to,
		});

		const preparedTimeAggregationType = isOranVendor
			? undefined
			: selectedTimeAggregationType;
		const granularity = isOranVendor && selectedTimeAggregationType in TimeAggregationToGranularityMap
			? TimeAggregationToGranularityMap[selectedTimeAggregationType as
				keyof typeof TimeAggregationToGranularityMap]
			: undefined;

		const drillDownData: IKpiChartsDTO = {
			from: start,
			to: end,
			aggregationType: aggregationType,
			kpiIds: [currentChart.kpiId],
			sites: currentChart.sites,
			timeAggregationType: preparedTimeAggregationType,
			vendor: currentChart.vendor.name,
			granularity,
		};

		if (!isOranVendor || isAvailableDrillDown) {
			getDrillDownCharts(drillDownData, () => {
				setCurrentIndexTimeAggregationType(currentIndexTimeAggregationType - 1);

				setDetailedDrillTime(prevState => ({
					...prevState,
					[selectedTimeAggregationType]: {
						from: start,
						to: end,
					},
				}));
			});
		}
	};

	const { availableGranularityDrillUp, isLoadingAvailableGranularityDrillUp } = useQueryAvailableGranularityDrillUp({
		isAvailableDrillUp: currentChart.isAvailableDrillUp,
		isOranVendor,
		from: currentChart.from,
		to: currentChart.to,
		granularity: currentChart.granularity?.name,
		vendor: currentChart.vendor.name,
	});

	const drillUp = () => {
		const selectedTimeAggregationType = listOfTimeAggregationTypes[currentIndexTimeAggregationType + 1];

		if (defaultIndexTimeAggregationType === currentIndexTimeAggregationType + 1) {
			setCurrentChart(chart);
			setCurrentIndexTimeAggregationType(currentIndexTimeAggregationType + 1);
			setDetailedDrillTime({});
		} else if (Object.keys(detailedDrillTime).includes(selectedTimeAggregationType)) {
			const prevTime = (detailedDrillTime as any)[selectedTimeAggregationType];
			const preparedTimeAggregationType = isOranVendor
				? undefined
				: selectedTimeAggregationType;
			const granularity = isOranVendor && selectedTimeAggregationType in TimeAggregationToGranularityMap
				? TimeAggregationToGranularityMap[selectedTimeAggregationType as
					keyof typeof TimeAggregationToGranularityMap]
				: undefined;

			const drillUpData: IKpiChartsDTO = {
				from: prevTime.from,
				to: prevTime.to,
				aggregationType: aggregationType,
				kpiIds: [currentChart.kpiId],
				sites: currentChart.sites,
				timeAggregationType: preparedTimeAggregationType,
				vendor: currentChart.vendor.name,
				granularity,
			};

			getDrillDownCharts(drillUpData, () => {
				setCurrentIndexTimeAggregationType(currentIndexTimeAggregationType + 1);
			});
		} else if (availableGranularityDrillUp) {
			const drillUpData: IKpiChartsDTO = {
				from: currentChart.from,
				to: currentChart.to,
				aggregationType: aggregationType,
				kpiIds: [currentChart.kpiId],
				sites: currentChart.sites,
				vendor: currentChart.vendor.name,
				granularity: availableGranularityDrillUp.name,
			};

			getDrillDownCharts(drillUpData, () => {
				const currentTimeAggregationType = GranularityToTimeAggregationMap[availableGranularityDrillUp.name];

				setCurrentIndexTimeAggregationType(currentIndexTimeAggregationType + 1);

				setDetailedDrillTime(prevState => ({
					...prevState,
					[currentTimeAggregationType]: {
						from: currentChart.from,
						to: currentChart.to,
					},
				}));
			});
		}

		setIsCheckedInterval(false);
		setDefaultIndexTimeAggregationTypeBreadcrumbs(prevState => prevState + 1);
	};

	useEffect(() => {
		// reset defaultIndexTimeAggregationTypeBreadcrumbs if the current chart is not available for drill up
		const isAvailableDrillUp = currentChart.isAvailableDrillUp;

		if (!isAvailableDrillUp && (currentChart.yaxisAggregationType.name === chart.yaxisAggregationType.name)) {
			setDefaultIndexTimeAggregationTypeBreadcrumbs(
				listOfTimeAggregationTypes.indexOf(chart.yaxisAggregationType.name),
			);
		}
	}, [chart.yaxisAggregationType.name, currentChart, defaultIndexTimeAggregationType]);

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const closeTypeSelectionForm = () => setIsOpenFileTypeSelectionModal(false);

	const exportHandler = (data: IExportFileDTO) => {
		if (!chartsReceivedAt) return;

		const preparedTimeAggregationType = isOranVendor ? undefined : currentChart.yaxisAggregationType.name;
		const granularity = isOranVendor ? currentChart.granularity?.name : undefined;

		const exportData: Array<IExportKpiDTO> = [{
			fileType: data.fileType,
			aggregationType: currentChart.aggregationType.name,
			from: currentChart.from,
			to: currentChart.to,
			lastSeenTime: chartsReceivedAt,
			kpiIds: [currentChart.kpiId],
			sites: currentChart.sites,
			timeAggregationType: preparedTimeAggregationType,
			vendor: currentChart.vendor.name,
			granularity,
		}];

		exportCharts(exportData);
	};

	return (
		<div className='f5gFullChartCardContainer'>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionCommonForm
					activeExportAction={ExportActionsTypes.all}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={exportHandler}
					isLoading={false}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			{(isLoadingDrillDownCharts || isLoadingAvailableGranularityDrillUp) && <SpinnerOverlay />}
			{!!chart.tags.length
				&& <div className='f5gFullChartCardTags'>
					<KpiTags tags={chart.tags} />
				</div>
			}
			{isShowSiteId
				&& <div className='f5gFullChartCardSiteId'>
					{isOranVendor ? 'Node' : 'Site'} ID: {siteId}
				</div>
			}
			<div className='f5gFullChartCardControlWrapper'>
				<CheckboxOptions
					chart={currentChart}
					isCheckedCompare={isCheckedCompare}
					isCheckedCorrelate={isCheckedCorrelate}
					isDisabledCompare={!!listOfTooltipCompareMessage.length}
					isDisabledCorrelate={!!listOfTooltipCorrelateMessage.length}
					listOfTooltipCompareMessage={listOfTooltipCompareMessage}
					listOfTooltipCorrelateMessage={listOfTooltipCorrelateMessage}
					toggleAddToCompare={toggleAddToCompare}
					toggleAddToCorrelate={toggleAddToCorrelate}
				/>
				{isWithInterval && isView15MinCheckbox &&
					<SliderCheckbox
						checked={isCheckedInterval}
						id='interval-slider-checkbox'
						label='15-minute interval'
						onChange={setIntervalData}
					/>
				}
			</div>
			<DateBreadcrumbs
				currentIndexTimeAggregationType={currentIndexTimeAggregationType}
				date={[firstDate, lastDate]}
				defaultIndexTimeAggregationType={defaultIndexTimeAggregationTypeBreadcrumbs}
				isMultiDays={isMultiDays}
				isOranVendor={isOranVendor}
			/>
			<div className='f5gFullChartCardWrapper'>
				<Chart
					charts={[currentChart]}
					isLegend={isMultiLines || isShowLegendByAggregationType}
					isLogarithmic={isLogarithmic}
					isMaintainAspectRatio={false}
					isThreshold={isThreshold}
					onClick={drillDown}
				/>
			</div>
			<ThresholdText
				chart={currentChart}
				isVisible={isThreshold}
			/>
			<ChartControlPanel
				chart={currentChart}
				charts={[chart]}
				downloadChart={toggleFileTypeSelectionModal}
				drillUp={drillUp}
				isBackIcon={
					!!currentChart?.isAvailableDrillUp
					|| defaultIndexTimeAggregationType > currentIndexTimeAggregationType}
				isCheckedLogarithmic={isLogarithmic}
				isCheckedThreshold={isThreshold}
				isDisabledThreshold={!thresholds.length}
				isFullCard
				prevTimeAggregationType={listOfTimeAggregationTypes[currentIndexTimeAggregationType + 1]}
				toggleLogarithmic={toggleLogarithmic}
				toggleThreshold={toggleThreshold}
			/>
		</div>
	);
};

export default memo(FullChartCard);
